import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface AffiliateLink {
  tool_slug: string;
  tool_name: string;
  affiliate_url: string | null;
  network: string | null;
  is_active: boolean;
}

let cache: Map<string, AffiliateLink> | null = null;
// Single in-flight promise so N simultaneous mounts (one per ToolCard)
// share ONE network request instead of stampeding the API. This was the
// 60+ duplicate /tool_affiliates requests Lighthouse flagged on /directory.
let inflight: Promise<Map<string, AffiliateLink>> | null = null;
const listeners = new Set<(m: Map<string, AffiliateLink>) => void>();

async function loadAffiliates() {
  const { data, error } = await supabase
    .from("tool_affiliates")
    .select("tool_slug, tool_name, affiliate_url, network, is_active")
    .eq("is_active", true);
  if (error) {
    console.error("Failed to load affiliates", error);
    return new Map<string, AffiliateLink>();
  }
  const map = new Map<string, AffiliateLink>();
  (data || []).forEach((row) => map.set(row.tool_slug, row as AffiliateLink));
  return map;
}

const ensureLoaded = () => {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = loadAffiliates().then((m) => {
    cache = m;
    inflight = null;
    listeners.forEach((cb) => cb(m));
    return m;
  });
  return inflight;
};

export const useAffiliates = () => {
  const [map, setMap] = useState<Map<string, AffiliateLink>>(cache || new Map());
  const [loading, setLoading] = useState(!cache);

  useEffect(() => {
    listeners.add(setMap);
    if (!cache) {
      ensureLoaded().then(() => setLoading(false));
    }
    return () => {
      listeners.delete(setMap);
    };
  }, []);

  const getAffiliate = (slug: string): AffiliateLink | undefined => map.get(slug);
  const hasAffiliate = (slug: string): boolean => {
    const a = map.get(slug);
    return !!(a && a.affiliate_url);
  };

  return { map, loading, getAffiliate, hasAffiliate };
};

export const refreshAffiliates = async () => {
  cache = await loadAffiliates();
  inflight = null;
  listeners.forEach((cb) => cb(cache!));
};
